// The single answer to "giver flytterengøring servicefradrag?".
//
// Until 12 August 2026 the site answered both ways. Four pages promised the
// visitor a deduction, two denied it flat out, the price guide did both on the
// same page, and the calculator quietly sided with "no". Every one of those was
// written as free prose, so nothing stopped them drifting apart. This module
// exists so the answer lives in one place and the pages import it instead of
// re-deriving it.
//
// THE RULE, and it is not about the type of cleaning:
//
//   Ligningslovens § 8 V, stk. 2, nr. 2 makes the deduction conditional on
//   "at arbejdet er udført vedrørende en helårsbolig, hvor den skattepligtige
//   person har fast bopæl på tidspunktet for arbejdets udførelse".
//   Repeated verbatim in BEK nr 391 af 28/03/2022 § 2, stk. 2, in Den juridiske
//   vejledning C.A.4.3.6 ("havde fast bopæl i helårsboligen på tidspunktet for
//   arbejdets udførelse"), and in plain Danish on skat.dk: "Du skal bo i boligen
//   (helårsbolig), mens arbejdet bliver udført."
//
//   The cleaning itself is squarely inside bilag 1, pkt. A ("Almindelig
//   rengøring": vask og aftørring af flader, rengøring af toilet og bad,
//   støvsugning, gulvvask og boning). Flytterengøring is exactly that work, and
//   no rule anywhere excludes one-off jobs or jobs tied to a move.
//
//   So the deciding question is WHERE THE CUSTOMER LIVES ON THE DAY, not what
//   the job is called:
//     - Fraflytningsrengøring, still resident at the address  -> deduction.
//       skat.dk is explicit that "det er ikke afgørende, hvornår du har solgt
//       eller opsagt din lejlighed".
//     - Indflytningsrengøring before moving in                -> no deduction.
//       Owning the home is not enough; ownership is the test for fritidsbolig
//       (§ 8 V, stk. 2, nr. 2, 2. led), never for a helårsbolig.
//
// Sources, verified 12 August 2026:
//   LL § 8 V + bilag 1  https://www.retsinformation.dk/eli/lta/2025/1500
//   BEK nr 391 af 2022  https://www.retsinformation.dk/eli/lta/2022/391
//   Juridisk vejl.      https://info.skat.dk/data.aspx?oid=2061725
//   skat.dk betingelser https://skat.dk/borger/fradrag/servicefradrag/generelle-betingelser
//
// What we deliberately do NOT claim: that SKAT has ruled on flytterengøring.
// There is no published afgørelse, bindende svar or LSR-kendelse on it. The
// answer above is read off the statute, not off a precedent.
//
// Amounts (cap, tax value) are NOT duplicated here — they live in
// lib/price-view.ts. Keep it that way.

/** The rule in one sentence. Use where there is no room for the full answer. */
export const FLYTTERENGOERING_FRADRAG_KORT =
  "Flytterengøring giver servicefradrag, hvis du stadig har fast bopæl i boligen den dag, der bliver gjort rent.";

export const FAQ_FLYTTERENGOERING_FRADRAG = {
  question: "Kan man trække flytterengøring fra i skat?",
  answer:
    "Det afhænger af, hvor du har fast bopæl den dag, der bliver gjort rent. Gør du rent i den bolig, du stadig bor i, kan du trække arbejdslønnen fra. Er du allerede flyttet, eller er du ikke flyttet ind i den nye bolig endnu, kan du ikke. Loftet er 18.300 kr. pr. person i 2026 og deles med al anden rengøring og havearbejde. Du skal betale elektronisk og selv indberette beløbet til SKAT.",
} as const;

export const FAQ_FRAFLYTNING_FRADRAG = {
  question: "Kan jeg få servicefradrag på fraflytningsrengøring?",
  answer:
    "Ja, hvis rengøringen bliver udført, mens du stadig har fast bopæl i boligen. Det afgørende er ikke, hvornår du har opsagt lejemålet eller solgt boligen, men hvor du bor den dag, der bliver gjort rent. Har du allerede meldt flytning til din nye adresse, kan du ikke trække den fra. Loftet er 18.300 kr. pr. person i 2026, og fradraget er ca. 26 % værd i sparet skat.",
} as const;

export const FAQ_INDFLYTNING_FRADRAG = {
  question: "Kan jeg få servicefradrag på indflytningsrengøring?",
  answer:
    "Sjældent. Servicefradraget følger den bolig, du har fast bopæl i den dag, der bliver gjort rent. Gør du rent i den nye bolig, før du er flyttet ind, er betingelsen ikke opfyldt. Det gælder også, selvom du har fået nøglerne eller allerede har købt boligen. Venter du med rengøringen til efter, du er flyttet ind, kan du trække arbejdslønnen fra.",
} as const;

/** Shown next to a flytterengøring price estimate, where the number is on screen. */
export const FLYTTERENGOERING_FRADRAG_NOTE =
  "Flytterengøring tæller kun med i servicefradraget, hvis du stadig har fast bopæl i boligen, den dag der bliver gjort rent. Gør du rent i en ny bolig, du ikke er flyttet ind i endnu, kan du ikke trække den fra.";

/** Dødsbo has no resident to meet the bopælskrav, so it never qualifies. */
export const DOEDSBO_FRADRAG_NOTE =
  "Rengøring af et dødsbo giver ikke servicefradrag. Fradraget kræver, at den der betaler, har fast bopæl i boligen, mens arbejdet bliver udført.";
